/** Shared validation helpers for application form questions (client). */
import {
  coerceDataGridAnswerValue,
  formatDataGridAnswerText,
  isDataGridQuestionType,
} from './dataGridAnswerDisplay';
import { formatLetteredRowAnswerText } from './applicationAnswerCoercion';

export function countWords(text) {
  const s = String(text ?? '').trim();
  if (!s) return 0;
  return s.split(/\s+/).filter(Boolean).length;
}

export function getOtherOptionLabel(question) {
  const cfg = question?.typeConfig && typeof question.typeConfig === 'object' ? question.typeConfig : {};
  const label = String(cfg.otherOptionLabel ?? 'Other').trim();
  return label || 'Other';
}

export function isOtherOptionValue(selection, question) {
  const other = getOtherOptionLabel(question);
  return String(selection || '').trim().toLowerCase() === other.toLowerCase();
}

export function normalizeSingleChoiceOtherValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return {
      selection: String(value.selection ?? ''),
      otherText: String(value.otherText ?? ''),
    };
  }
  if (typeof value === 'string') return { selection: value, otherText: '' };
  return { selection: '', otherText: '' };
}

export function normalizeMultipleChoiceOtherValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const selections = Array.isArray(value.selections) ? value.selections.map(String) : [];
    return { selections, otherText: String(value.otherText ?? '') };
  }
  if (Array.isArray(value)) return { selections: value.map(String), otherText: '' };
  return { selections: [], otherText: '' };
}

export function formatDisplayValue(value, question = null) {
  if (value == null || value === '') return '';
  const qType = String(question?.type || '').toUpperCase();
  if (qType === 'PERCENTAGE') {
    const s = String(value).trim();
    if (!s) return '';
    const rawMax = question?.validationRules?.max;
    if (rawMax === -1) return `${s}%`;
    const max = Number.isFinite(Number(rawMax)) && Number(rawMax) > 0 ? Number(rawMax) : 100;
    return `${s}% / ${max}%`;
  }
  if ((qType === 'LABELED_LIST' || qType === 'TABLE_ROWS') && Array.isArray(value)) {
    return formatLetteredRowAnswerText(value, question);
  }
  if (question && isDataGridQuestionType(question)) {
    return formatDataGridAnswerText(coerceDataGridAnswerValue(value, question), question);
  }
  if (Array.isArray(value)) {
    if (value.length && value.every((v) => v && typeof v === 'object' && !Array.isArray(v))) {
      return formatDataGridAnswerText(value, question || {});
    }
    return value.filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    if ('text' in value) return String(value.text ?? '').trim();
    if ('selection' in value) {
      const v = normalizeSingleChoiceOtherValue(value);
      const otherLabel = question ? getOtherOptionLabel(question) : 'Other';
      if (String(v.selection || '').trim().toLowerCase() === otherLabel.toLowerCase()) {
        return String(v.otherText || v.selection || '').trim();
      }
      return String(v.selection || '').trim();
    }
    if ('selections' in value) {
      const v = normalizeMultipleChoiceOtherValue(value);
      const otherLabel = question ? getOtherOptionLabel(question) : 'Other';
      const parts = v.selections.map((s) => {
        if (String(s || '').trim().toLowerCase() === otherLabel.toLowerCase()) {
          return v.otherText ? `${otherLabel}: ${v.otherText}` : String(s);
        }
        return String(s);
      });
      return parts.filter(Boolean).join(', ');
    }
    if ('start' in value || 'end' in value) {
      const start = value.start || '';
      const end = value.end || '';
      return [start, end].filter(Boolean).join(' – ');
    }
    const qType = String(question?.type || '').toUpperCase();
    if (qType === 'SIDE_BY_SIDE_CHOICE') {
      const cfg = question?.typeConfig || {};
      const columns = Array.isArray(cfg.columns) ? cfg.columns : [];
      const lines = columns.map((c, i) => {
        const key = typeof c === 'object' ? String(c.key || `col${i + 1}`) : `col${i + 1}`;
        const label = typeof c === 'object' ? String(c.label || key) : String(c);
        const sel = value[key];
        return `${label}: ${sel != null && String(sel).trim() ? String(sel).trim() : '—'}`;
      });
      return lines.filter(Boolean).join('\n');
    }
  }
  return String(value);
}

export function resolvePipedSourceValue(sourceFieldKey, answersContext = {}, sourceQuestion = null) {
  const key = String(sourceFieldKey || '').trim();
  if (!key) return '';
  const raw = answersContext[key];
  const q = sourceQuestion
    || answersContext?.__questionsByKey?.[key]
    || null;
  return formatDisplayValue(raw, q);
}

export function evalVisibilityRule(rule = {}, answersByField = {}) {
  const source = String(rule?.sourceFieldKey || '').trim();
  const op = String(rule?.operator || 'EQUALS').toUpperCase();
  const expected = rule?.value;
  const actual = answersByField[source];
  if (op === 'IS_EMPTY') return actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0);
  if (op === 'IS_NOT_EMPTY') return !(actual == null || actual === '' || (Array.isArray(actual) && actual.length === 0));
  if (op === 'EQUALS') return actual === expected;
  if (op === 'NOT_EQUALS') return actual !== expected;
  if (op === 'IN') return Array.isArray(expected) ? expected.includes(actual) : false;
  if (op === 'NOT_IN') return Array.isArray(expected) ? !expected.includes(actual) : true;
  if (op === 'CONTAINS') {
    return Array.isArray(actual) ? actual.includes(expected) : String(actual || '').includes(String(expected ?? ''));
  }
  if (op === 'NOT_CONTAINS') {
    return Array.isArray(actual) ? !actual.includes(expected) : !String(actual || '').includes(String(expected ?? ''));
  }
  return true;
}

function questionOwnVisibilityKeys(question = {}) {
  return new Set(
    [String(question?.fieldKey || '').trim(), String(question?.id || '').trim()].filter(Boolean)
  );
}

/** Drop rules that reference the question's own field — they can never show on an empty form. */
function effectiveVisibilityRules(question = {}) {
  const rules = Array.isArray(question?.visibilityRules) ? question.visibilityRules : [];
  if (!rules.length) return rules;
  const own = questionOwnVisibilityKeys(question);
  return rules.filter((r) => !own.has(String(r?.sourceFieldKey || '').trim()));
}

export function isQuestionVisible(question = {}, answersByField = {}) {
  const rules = effectiveVisibilityRules(question);
  if (!rules.length) return true;
  return rules.every((r) => evalVisibilityRule(r, answersByField));
}

export function getWordCountLimits(question) {
  const rules = question?.validationRules || {};
  const minWords = Number.isFinite(Number(rules.minWords)) ? Number(rules.minWords) : null;
  const maxWords = Number.isFinite(Number(rules.maxWords)) ? Number(rules.maxWords) : null;
  return { minWords, maxWords };
}

export function wordCountError(question, text) {
  const { minWords, maxWords } = getWordCountLimits(question);
  const count = countWords(text);
  if (maxWords != null && count > maxWords) {
    return `Maximum ${maxWords} words (${count} entered).`;
  }
  if (minWords != null && count > 0 && count < minWords) {
    return `Minimum ${minWords} words (${count} entered).`;
  }
  if (minWords != null && count === 0) return null;
  return null;
}

export function getSelectionCountLimits(question) {
  const rules = question?.validationRules || {};
  const minSelections = Number.isFinite(Number(rules.minSelections)) ? Number(rules.minSelections) : null;
  const maxSelections = Number.isFinite(Number(rules.maxSelections)) ? Number(rules.maxSelections) : null;
  return { minSelections, maxSelections };
}

/** Percentage questions always start at 0. max: -1 means unlimited; missing max defaults to 100. */
export function getPercentageLimits(question) {
  const raw = question?.validationRules?.max;
  if (raw === -1) return { min: 0, max: null };
  if (Number.isFinite(Number(raw)) && Number(raw) > 0) return { min: 0, max: Number(raw) };
  return { min: 0, max: 100 };
}

export function formatPercentageRangeHint(question) {
  const { min, max } = getPercentageLimits(question);
  if (max == null) return `Applicants enter a number from ${min} with no upper limit. The % symbol is shown automatically.`;
  return `Applicants enter a number between ${min} and ${max}. The % symbol is shown automatically.`;
}

export function percentageRangeError(question, value) {
  if (value == null || String(value).trim() === '') return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Enter a valid number.';
  const { min, max } = getPercentageLimits(question);
  if (num < min) return `Minimum is ${min}%.`;
  if (max != null && num > max) return `Maximum is ${max}%.`;
  return null;
}
